import React, { useState, memo, useMemo } from 'react';
import styled from 'styled-components';
import { OverlayTrigger, Popover, Table, Alert, Form } from '../ui-kit';
import { IconSpinner, IconExclamationTriangle } from '../ui-kit/icons';
import Loader from '../general/Loader';
import { formatTime } from '../../utils/time';
import moment from 'moment';

const CenteredCell = styled.div`
  text-align: center;
`;

const SkillAuditTable = ({ certifications, anyFuture }) => (
  <>
    {anyFuture && <Alert variant="warning">{I18n.t('group_skills.future_certifications_warning')}</Alert>}
    <Table bordered condensed className="p-0 m-0">
      <thead>
        <tr>
          <th>{I18n.t('activerecord.attributes.certification.start_time')}</th>
          <th>{I18n.t('activerecord.attributes.certification.end_time')}</th>
          <th>{I18n.t('attributes.comments')}</th>
          <th>{I18n.t('activerecord.attributes.certification.modified_by_user')}</th>
        </tr>
      </thead>
      <tbody>
        {certifications.length === 0 && (
          <tr>
            <td colSpan="4" className="text-center">
              <i>{I18n.t('no_results_found')}</i>
            </td>
          </tr>
        )}
        {certifications &&
          certifications.map(c => (
            <tr key={c.id}>
              <td>{formatTime(c.start_time)}</td>
              <td>{formatTime(c.end_time) || <i>{I18n.t('not_applicable_abbr')}</i>}</td>
              <td>
                <small>{c.comments}</small>
              </td>
              <td>
                <small>{c.modified_by_user_name}</small>
              </td>
            </tr>
          ))}
      </tbody>
    </Table>
  </>
);

const SkillCheckboxTableCell = ({
  certifications,
  membershipId,
  skillId,
  userName,
  skillName,
  assignSkill,
  onContextMenu
}) => {
  const [loading, setLoading] = useState(false);

  const now = moment();

  // Returns true iff there are any changes to certifications scheduled for the
  // future. This means that a skill will start or be revoked in the future
  const anyFutureCertifications = certifications.some(
    c => now.isBefore(c.start_time) || (c.end_time && now.isBefore(c.end_time))
  );

  const popover = useMemo(
    () => (
      <Popover id={membershipId} style={{ width: '40rem' }}>
        <Popover.Header>{I18n.t('skills.audit') + ' - ' + userName + '\n' + skillName}</Popover.Header>
        <Popover.Body className="p-0">
          {!certifications && <Loader />}
          {certifications && <SkillAuditTable certifications={certifications} anyFuture={anyFutureCertifications} />}
        </Popover.Body>
      </Popover>
    ),
    [certifications, anyFutureCertifications, membershipId, userName, skillName]
  );

  const checked = certifications.some(c =>
    c.end_time ? now.isBetween(c.start_time, c.end_time) : now.isAfter(c.start_time)
  );

  const toggleChecked = async () => {
    setLoading(true);
    await assignSkill({ membershipId, skillId, possessed: !checked });
    setLoading(false);
  };

  const openActionMenu = event => {
    event.preventDefault();
    event.stopPropagation();
    onContextMenu(event, membershipId, skillId);
  };

  return (
    <CenteredCell onClick={toggleChecked} onContextMenu={event => openActionMenu(event)}>
      {loading && <IconSpinner spin />}
      {!loading && (
        <OverlayTrigger overlay={popover}>
          <span>
            <Form.Check checked={checked} readOnly={true} className="pe-none" />{' '}
            {anyFutureCertifications && (
              <IconExclamationTriangle
                title={I18n.t('skills.future_certifications_warning')}
                aria-label={I18n.t('skills.future_certifications_warning')}
              />
            )}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 ms-1 align-baseline sr-only"
              onClick={openActionMenu}
              aria-label={I18n.t('skills.audit')}
            >
              <span aria-hidden="true">⋮</span>
            </button>
          </span>
        </OverlayTrigger>
      )}
    </CenteredCell>
  );
};

export default memo(SkillCheckboxTableCell);
